
import { supabase } from './supabase';
import { Profile, UserRole } from '../types/user';

/**
 * Get all users from the database
 */
export const getAllUsers = async (): Promise<Profile[]> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching users:', error);
      return [];
    }

    return data as Profile[];
  } catch (error) {
    console.error('Error fetching users:', error);
    return [];
  }
};

/**
 * Get user by ID
 */
export const getUserById = async (id: string): Promise<Profile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data as Profile;
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

/**
 * Get user by email
 */
export const getUserByEmail = async (email: string): Promise<Profile | null> => {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('email', email)
      .single();

    if (error) {
      console.error('Error fetching user:', error);
      return null;
    }

    return data as Profile;
  } catch (error) {
    console.error('Error fetching user:', error);
    return null;
  }
};

/**
 * Update user profile
 */
export const updateUser = async (userId: string, updates: Partial<Profile>): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('profiles')
      .update(updates as any)
      .eq('id', userId);

    if (error) {
      console.error('Error updating user:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error updating user:', error);
    return false;
  }
};

/**
 * Create a new user (Admin only)
 * This requires Supabase Admin API or a database function
 */
export const createUser = async (userData: {
  email: string;
  password: string;
  full_name: string;
  role?: UserRole;
  calcom_url?: string | null;
}): Promise<{ success: boolean; error?: string; userId?: string }> => {
  try {
    // Create auth user using Supabase Admin API
    // Note: This requires admin privileges or a server-side function
    // For now, we'll use the sign-up method and then update the profile

    const { data, error } = await supabase.auth.signUp({
      email: userData.email,
      password: userData.password,
      options: {
        data: {
          full_name: userData.full_name,
          role: userData.role || 'user',
        },
        emailRedirectTo: undefined,
      }
    });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    if (!data.user) {
      return {
        success: false,
        error: 'Failed to create user'
      };
    }

    // Create profile in profiles table
    const { error: profileError } = await supabase
      .from('profiles')
      .insert({
        id: data.user.id,
        email: userData.email,
        full_name: userData.full_name,
        role: userData.role || 'user',
        is_active: true,
        created_at: new Date().toISOString(),
        calcom_url: userData.calcom_url || null,
      });

    if (profileError) {
      console.error('Error creating profile:', profileError);
      // User was created in auth but profile failed - clean up
      await supabase.auth.admin.deleteUser(data.user.id);
      return {
        success: false,
        error: 'Failed to create user profile: ' + profileError.message
      };
    }

    return {
      success: true,
      userId: data.user.id
    };
  } catch (error: any) {
    console.error('Error creating user:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Reset user password (generates a password reset link)
 */
export const resetUserPassword = async (email: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error resetting password:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Update user password (requires current session)
 */
export const updatePassword = async (newPassword: string): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      return {
        success: false,
        error: error.message
      };
    }

    return { success: true };
  } catch (error: any) {
    console.error('Error updating password:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred'
    };
  }
};

/**
 * Get active users count
 */
export const getActiveUsersCount = async (): Promise<number> => {
  try {
    const { count, error } = await supabase
      .from('profiles')
      .select('*', { count: 'exact', head: true })
      .eq('is_active', true);

    if (error) {
      console.error('Error counting active users:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error counting active users:', error);
    return 0;
  }
};
